/**
 * Shared compliance status counting utilities. Used by ComplianceDataContext
 * to avoid duplicating the bucketing logic.
 */

/**
 * Minimal shape this module needs to bucket a requirement. Structural rather than tied to
 * RequirementWithStatus so vehicle requirement rows — which carry the same field under the same
 * name — go through identical bucketing instead of a parallel implementation.
 */
export interface CountableRequirement {
  complianceStatus?: string | undefined;
}

/** Bucket counts returned by countRequirementAlerts. */
export interface RequirementAlertCounts {
  error: number;
  warning: number;
  /** Retired category — always 0. See the bucketing rules on countRequirementAlerts. */
  incomplete: number;
  complete: number;
}

/**
 * Counts a flat array of requirement records into alert buckets. Accepts anything carrying
 * complianceStatus, so scope requirements and vehicle requirement rows share one implementation.
 *
 * Bucketing rules:
 *  error      – Failed | Rejected | Expired | Past Due | Non-Compliant | Not Started | In-Complete
 *  warning    – Warning | Expires Soon | Expiring Soon | Due Soon | Submitted
 *  incomplete – always 0. The In-Complete category is retired: Not Started and In-Complete now
 *               count as error, Submitted as warning. The bucket is kept so the DashboardAlerts
 *               shape (and its chip/badge) stays intact while every producer reports zero.
 *  complete   – Passed | Compliant
 *
 * Handles both old Pascal-case API values (e.g. 'Failed') and newer string literals
 * (e.g. 'In-Complete'). The compliance category alone decides the bucket: evidence status is not
 * consulted, because the alert chips report a single count per category rather than a per-reason
 * breakdown.
 */
export function countRequirementAlerts(
  requirements: ReadonlyArray<CountableRequirement>
): RequirementAlertCounts {
  const counts: RequirementAlertCounts = {
    error: 0,
    warning: 0,
    incomplete: 0,
    complete: 0,
  };

  for (const { complianceStatus } of requirements) {
    // Normalize complianceStatus: lowercase, remove spaces/hyphens
    const statusNorm = String(complianceStatus ?? '')
      .trim()
      .toLowerCase()
      .replaceAll(' ', '')
      .replaceAll('-', '');

    switch (statusNorm) {
      case 'failed':
      case 'rejected':
      case 'expired':
      case 'pastdue':
      case 'noncompliant':
      case 'noncompliance':
      case 'noncompliantrequirement':
      // The In-Complete category is retired. These values only reach here on requirements assessed
      // before the rule change, and they must bucket the same way the Requirements-view alert cards
      // bucket them (see OUT_OF_COMPLIANCE_CATEGORIES) — otherwise the same requirement reads red on
      // one screen and blue on the other.
      case 'notstarted':
      case 'incomplete':
        counts.error++;
        break;
      case 'warning':
      case 'expiressoon':
      case 'expiringsoon':
      case 'duesoon':
      // Submitted and awaiting a reviewer — the same lifecycle stage as In Review, which warns.
      case 'submitted':
        counts.warning++;
        break;
      case 'passed':
      case 'compliant':
        counts.complete++;
        break;
      case 'notapplicable':
      default:
        break;
    }
  }

  return counts;
}
